import React, { useState, useRef, useEffect } from 'react';
import { supabase } from '../../supabaseClient.js';
import Grid from '@mui/material/Grid2';
import { Container, IconButton, Typography, TextField, Stack, Button, Link } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import InputAdornment from '@mui/material/InputAdornment';
import { useDispatch } from 'react-redux';
import { setLoginStatus, setUserDetails, setCurrentOrg, setAllOrgs, setRole } from '../../store.js';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import BrandLogo from '../../components/BrandLogo';
import useAlert from '../../alert/useAlert.js';
import { useSelector, shallowEqual } from 'react-redux';

const LoginScreen = () => {
  const [currentScreen, setCurrentScreen] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginDetailsSubmitted, setLoginDetailsSubmitted] = useState(false);
  const [emailSubmitted, setEmailSubmitted] = useState(false);
  const [resetOutcome, setResetOutcome] = useState(null);
  const [resetSending, setResetSending] = useState(false);
  const resetInFlight = useRef(false);
  const [showPassword, setShowPassword] = useState(false);
  const hasMounted = useRef(false);
  const { setAlert } = useAlert();

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { isLoggedIn } = useSelector((state) => ({ isLoggedIn: state.globalState.isLoggedIn }), shallowEqual);

  useEffect(() => {
    if (hasMounted.current) { return; }
    if(isLoggedIn) {
      navigate('/consultation');
      return;
    }
    dispatch(setCurrentOrg(null));
    dispatch(setAllOrgs(null));
    dispatch(setRole(null));
    dispatch(setLoginStatus(false));
    dispatch(setUserDetails(null));
    hasMounted.current = true;
  }, []);

  useEffect(() => {
    setLoginDetailsSubmitted(false);
    setEmailSubmitted(false);
  }, [ currentScreen ]);

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const getUserDetails = async (id) => {
    let { data, error } = await supabase.from('user_accounts').select('*').eq('id', id);
    if (error) {
      console.error("Error: ", error);
      return null;
    } else if(data.length === 0) {
      return null;
    }
    else {
      const user = data[0];
      const [name, email, practicetype] = await Promise.all([
        supabase.rpc('decrypt_text', { encrypted_data: user.name }),
        supabase.rpc('decrypt_text', { encrypted_data: user.email }),
        supabase.rpc('decrypt_text', { encrypted_data: user.practicetype }),
      ]);
      if(name.error || email.error || practicetype.error) {
        return user;
      }
      user.name = name.data;
      user.email = email.data;
      user.practicetype = practicetype.data;
      return user;
    }
  }

  const handleLogin = async () => {
    if(!email || !password) {
      setAlert('Email and password are required', 'error');
      return;
    }
    const { data, error } = await supabase.auth.signInWithPassword({
      email: email,
      password: password,
    });
    
    if (error) {
      setAlert(error.message, 'error');
    } else {
      const userDetails = await getUserDetails(data.user.id);
      if(!userDetails) {
        // A confirmed auth user with no profile row is a stranded signup
        // (the pre-trigger client-side insert misfired). Recreate the row —
        // onboarding_step takes its DEFAULT 1 — and resume the wizard,
        // instead of dead-ending an email that re-signup would reject as
        // already registered.
        const { error: healError } = await supabase
          .from('user_accounts')
          .insert([{ id: data.user.id, email: data.user.email, created_at: data.user.created_at }]);
        if (healError) {
          console.error('Failed to restore missing profile:', healError);
          await supabase.auth.signOut();
          setAlert('Unable to load your account. Please contact support.', 'error');
          return;
        }
        navigate('/onboarding');
        return;
      }
      // Unfinished onboarding resumes at the saved wizard step (NULL = done;
      // all pre-wizard accounts were backfilled NULL).
      if (userDetails.onboarding_step !== null && userDetails.onboarding_step !== undefined) {
        navigate('/onboarding');
        return;
      }
      const { teamIDs, teamNames } = await getAllTeams(data.user.id);
      if(teamNames.length === 0) {
        // Account exists but signup was abandoned before team creation
        // (legacy pre-wizard account, backfilled NULL): re-enter the wizard
        // at step 1 — without this write /onboarding would bounce back here.
        await supabase.from('user_accounts').update({ onboarding_step: 1 }).eq('id', data.user.id);
        navigate('/onboarding');
        return;
      }
      // `mode` is no longer user-selectable (Settings stamps 'Mental Health'), but
      // pre-wizard accounts were never backfilled, so a NULL here trapped them behind
      // the "Profile Setup Required" overlay on every page with no way to clear it.
      let mode = userDetails.mode;
      if (!mode) {
        mode = 'Mental Health';
        const { error: modeError } = await supabase.from('user_accounts').update({ mode }).eq('id', data.user.id);
        if (modeError) console.error('Failed to set default mode:', modeError);
      }
      const role = await getUserRole(data.user.id, teamIDs[0]);
      dispatch(setCurrentOrg({id: teamIDs[0], name: teamNames[0]}));
      dispatch(setAllOrgs(
        teamNames.map(name => ({
          id: teamIDs[teamNames.indexOf(name)],
          name: name
        }))
      ));
      dispatch(setRole(role));
      dispatch(setLoginStatus(true));
      dispatch(setUserDetails({ id: data.user.id, email: userDetails.email, name: userDetails.name, practicetype: userDetails.practicetype, mode, data_retention_days: userDetails.data_retention_days }));
      navigate('/consultation');
    }
  };

  const getAllTeams = async (id) => {
    const { data: teams, error } = await supabase.from('team_users').select('team_id').eq('user_id', id);
    if (error || !teams) {
      console.error('Error fetching teams: ', error?.message);
      return { teamIDs: [], teamNames: [] };
    }
    const teamIDs = teams.filter(team => team?.team_id).map(team => team.team_id);
    const teamNames = await getTeamNames(teamIDs);
    return { teamIDs, teamNames };
  }

  const getTeamNames = async (teamIDs) => {
    try {
      const teamNamePromises = teamIDs.map(async (team_id) => {
        const { data, error } = await supabase.from('teams').select('name').eq('id', team_id);
        if (error) {
          throw new Error(`Error fetching team name for team_id ${team_id}: ${error.message}`);
        }
        const decryptedTeamName = await supabase.rpc('decrypt_text', { encrypted_data: data[0].name });
        if (decryptedTeamName.error) {
          throw new Error(`Error decrypting team name for team_id ${team_id}: ${decryptedTeamName.error.message}`);
        }
        return decryptedTeamName.data;
      });

      const teamNames = await Promise.all(teamNamePromises);
      return teamNames;
    } catch (error) {
      console.error("Error: ", error);
      return [];
    }
  }

  const getUserRole = async (id, teamID) => {
    const { data, error } = await supabase.from('team_users').select('role').eq('user_id', id).eq('team_id', teamID);
    if (error) {
      console.error("Error: ", error);
      return;
    }
    return data[0].role;
  }
  

  const handleSendResetLink = async () => {
    const normalizedEmail = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)) {
      setAlert('Please enter a valid email address.', 'error');
      return;
    }
    if (resetInFlight.current) return;
    resetInFlight.current = true;
    setResetSending(true);
    setResetOutcome(null);
    try {
      // Eligibility is checked on the server before Supabase recovery is sent.
      // Never fall back to direct recovery if the check fails.
      const { data, error } = await supabase.functions.invoke('request-scribe-password-reset', {
        body: { email: normalizedEmail, redirectTo: process.env.REACT_APP_PASSWORD_RESET_LINK },
      });
      if (error || !['sent', 'ineligible'].includes(data?.outcome)) {
        throw new Error('Password reset request could not be verified');
      }
      setResetOutcome(data.outcome);
      setCurrentScreen('resetPassword');
    } catch {
      setAlert('Unable to verify your account or send a reset link. Please try again later or contact support.', 'error');
    } finally {
      resetInFlight.current = false;
      setResetSending(false);
    }
  };

  return (
    <Container disableGutters maxWidth={false}> 
      <Grid container spacing={2} sx={{ minHeight: '100vh', minWidth: '100vw', marginTop: -3 }}>
        <Grid size={12} sx={{ backgroundColor: '#B9F4F2', height: '90px', padding: '15px 10px 0px', display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
          <BrandLogo onClick={() => { navigate('/'); }} style={{cursor: 'pointer'}}/>
        </Grid>
        {
          currentScreen === 'login' ?
            <Grid size={12} sx={{display: 'flex', alignItems: 'flex-start', justifyContent: 'center'}}>
              <Stack spacing={3} width={{xs: '80vw', md: '40vw'}} direction="column" alignItems="stretch" justifyContent="center" style={{backgroundColor: '#f5f5f5', padding: '30px', borderRadius: '10px', boxShadow: '0 0 10px rgba(0, 0, 0, 0.2)' }}>
                <Typography variant="h6" sx={{fontWeight: 'bold', fontSize: '30px', marginBottom: '20px'}}>Login</Typography>
                <Typography variant="body1"> Don’t have an account? <span onClick={() => { navigate('/signup') }} style={{ cursor: 'pointer', color: '#29C0BB' }}>Sign Up</span> </Typography>
                <form>
                <TextField
                  required
                  fullWidth
                  label="Email"
                  variant="outlined"
                  focused
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email" 
                  sx={{ marginTop: '20px', marginBottom: '16px', backgroundColor: '#f5f5f5' }} 
                  error={loginDetailsSubmitted && !email}
                  helperText={loginDetailsSubmitted && !email && "Email is required"}
                />
                <TextField
                  required
                  fullWidth
                  label="Password"
                  variant="outlined"
                  focused
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password"
                  type={showPassword ? "text" : "password"}
                  sx={{ marginTop: '20px', marginBottom: '16px', backgroundColor: '#f5f5f5' }}
                  error={loginDetailsSubmitted && !password}
                  helperText={loginDetailsSubmitted && !password && "Password is required"}
                  slotProps={{
                    input: {
                      endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowPassword}
                          edge="end"
                        >
                          {showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                        </IconButton>
                      </InputAdornment>
                      ),
                    }
                  }}
                />
                </form>
                <Typography style={{marginBottom: '20px'}} variant="body1"> <span onClick={() => setCurrentScreen('forgotPassword')} style={{ cursor: 'pointer', color: '#29C0BB' }}>Forgot Password?</span> </Typography>
                <Button onClick={() => { setLoginDetailsSubmitted(true); handleLogin(); }  } variant="outlined" sx={{width: '100%', height: '50px', borderRadius: 30, backgroundColor: '#29C0BB', border: 0}}>
                  <Typography variant="button" sx={{color: '#FFFFFF', fontSize: '15px'}}>Login</Typography>
                </Button>
              </Stack>
            </Grid>
          : currentScreen === 'forgotPassword' ?
            <Grid size={12} sx={{display: 'flex', alignItems: 'flex-start', justifyContent: 'center'}}>
              <Stack spacing={3} width={{xs: '80vw', md: '40vw'}} direction="column" alignItems="flex-start" justifyContent="center" style={{backgroundColor: '#f5f5f5', padding: '30px', borderRadius: '10px', boxShadow: '0 0 10px rgba(0, 0, 0, 0.2)' }}>
                <Typography variant="h6" sx={{fontWeight: 'bold', fontSize: '30px'}}>Forgot Password</Typography>
                <Typography variant="body1">Remember your password? <span onClick={() => setCurrentScreen('login')} style={{ cursor: 'pointer', color: '#29C0BB' }}>Login</span> </Typography>
                <TextField
                  required
                  fullWidth
                  label="Email"
                  variant="outlined"
                  focused
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email" 
                  sx={{ marginTop: '20px', marginBottom: '20px', backgroundColor: '#f5f5f5' }} 
                  error={emailSubmitted && !email}
                  helperText={emailSubmitted && !email && "Email"}
                />
                <Button disabled={resetSending} aria-busy={resetSending} onClick={() => { setEmailSubmitted(true); handleSendResetLink(); }} variant="outlined" sx={{width: '100%', height: '50px', borderRadius: 30, backgroundColor: '#29C0BB', border: 0}}>
                  <Typography variant="button" sx={{color: '#FFFFFF', fontSize: '15px'}}>Send reset link</Typography>
                </Button>
              </Stack>
            </Grid>          
          : 
          currentScreen === 'resetPassword' ?
          <Grid size={12} sx={{display: 'flex', alignItems: 'flex-start', justifyContent: 'center'}}>
            <Stack spacing={3} width={{xs: '80vw', md: '40vw'}} direction="column" alignItems="flex-start" justifyContent="center" style={{backgroundColor: '#f5f5f5', padding: '30px', borderRadius: '10px', boxShadow: '0 0 10px rgba(0, 0, 0, 0.2)' }}>
              <Typography
                variant={resetOutcome === 'ineligible' ? 'body1' : 'h6'}
                role="status"
                sx={resetOutcome === 'ineligible' ? {
                  backgroundColor: '#f2dede',
                  border: '1px solid #ebccd1',
                  borderRadius: '4px',
                  padding: '16px 20px',
                  color: '#a94442',
                  fontSize: '18px',
                  lineHeight: 1.5,
                } : { fontWeight: 'bold', fontSize: '30px' }}
              >
                {resetOutcome === 'ineligible' ? <>
                  You currently do not have an account on Sophie Assist.{' '}
                  <br />
                  <Link component={RouterLink} to="/signup" sx={{color: '#29C0BB'}}>Click here</Link>{' '}
                  to create an account
                </> : 'A password reset link has been sent to your email.'}
              </Typography>
              <Typography variant="body1">Remember your password? <span onClick={() => setCurrentScreen('login')} style={{ cursor: 'pointer', color: '#29C0BB' }}>Login</span> </Typography>
            </Stack>
          </Grid>  
        : null                   
        }
      </Grid>
    </Container>
  );
};

export default LoginScreen;

